import { FunctionComponent, useEffect, useState } from 'react';
import {
  COUNTRIES,
  OrderStatusTranslations,
  OrderDetailsTranslations,
  OrderDetails as OrderDetailsReusable,
  OrderDetailsMode,
} from '@cdaas/reusable-components-daas';
import { useTranslation } from 'react-i18next';
import { BodyText } from '@gaiads/telia-react-component-library';
import { useDispatch } from 'react-redux';
import { ApprovalChainResponse, ApprovalChainStepResponse, ApproverType } from '@cdaas/api-clients/dist/asset-user-openapi';
import { ProductOrder } from '@cdaas/api-clients/dist/order-openapi';

import { getApprovalChainOfOrder, getApproverUserGroupRequest } from '../../utils';
import { modalShow, modalClose } from '../../store/actions';

interface OrderDetailsProps {
  order: ProductOrder;
}

const OrderDetails: FunctionComponent<OrderDetailsProps> = ({ order }) => {
  const ordersTranslations = useTranslation('orders').t;
  const approvalTranslations = useTranslation('approval').t;
  const globalTranslation = useTranslation('globalComponents').t;

  const [approvalChain, setApprovalChain] = useState<ApprovalChainResponse>();

  const dispatch = useDispatch();

  const orderStatusTranslations: OrderStatusTranslations = {
    approved: ordersTranslations('orderStatuses.approved'),
    processingOrder: ordersTranslations('orderStatuses.processingOrder'),
    delivered: ordersTranslations('orderStatuses.delivered'),
    pendingApproval: ordersTranslations('orderStatuses.pendingApproval'),
    financingDeclined: ordersTranslations('orderStatuses.financingDeclined'),
    approvalDeclined: ordersTranslations('orderStatuses.approvalDeclined'),
    cannotBeProcessed: ordersTranslations('orderStatuses.cannotBeProcessed'),
    cancelled: ordersTranslations('orderStatuses.cancelled'),
    returned: ordersTranslations('orderStatuses.returned'),
    discarded: ordersTranslations('orderStatuses.discarded'),
    draft: ordersTranslations('orderStatuses.draft'),
  };

  const orderDetailsTranslations: OrderDetailsTranslations = {
    orderId: ordersTranslations('orderDetails.orderId'),
    approvalChain: ordersTranslations('orderDetails.approvalChain'),
    price: ordersTranslations('orderDetails.price'),
    leasePeriod: ordersTranslations('orderDetails.leasePeriod'),
    hardware: ordersTranslations('orderDetails.hardware'),
    services: ordersTranslations('orderDetails.services'),
    insurance: ordersTranslations('orderDetails.insurance'),
    insuranceUrl: process.env.REACT_APP_INSURANCE_URI || '',
    user: ordersTranslations('orderDetails.user'),
    purchaser: ordersTranslations('orderDetails.purchaser'),
    delivery: ordersTranslations('orderDetails.delivery'),
    month: ordersTranslations('orderDetails.month'),
    months: ordersTranslations('orderDetails.months'),
    includingVat: ordersTranslations('orderDetails.includingVat'),
    excludingVat: ordersTranslations('orderDetails.excludingVat'),
    productImageAltText: ordersTranslations('orderDetails.productImageAltText'),
    orderStatusTranslations: orderStatusTranslations,
    approvalStatuses: {
      pending: approvalTranslations('ApprovalList.orderStatusOptions.PendingApproval'),
      approved: approvalTranslations('ApprovalList.orderStatusOptions.Approved'),
      rejected: approvalTranslations('ApprovalList.orderStatusOptions.Rejected'),
    },
    countries: {
      Finland: globalTranslation('countries.Finland'),
      Norway: globalTranslation('countries.Norway'),
      Sweden: globalTranslation('countries.Sweden'),
    },
    customerSupportName: ordersTranslations('orderDetails.customerSupportName'),
    companyDevice: ordersTranslations('orderDetails.companyDevice'),
    approvalStatusTranslations: {
      notNeeded: approvalTranslations('approvalStatuses.notNeeded'),
      pending: approvalTranslations('approvalStatuses.pending'),
      autoApproved: approvalTranslations('approvalStatuses.autoApproved'),
      approved: approvalTranslations('approvalStatuses.approved'),
      rejected: approvalTranslations('approvalStatuses.rejected'),
    },
  };

  useEffect(() => {
    const getApprovalChain = async () => {
      if (order.customer_approval.approvalChainId) {
        const resp = await getApprovalChainOfOrder(order.customer_approval.approvalChainId);
        if (resp) {
          setApprovalChain(resp);
        }
      }
    };
    getApprovalChain();
  }, [order.customer_approval]);

  const showUserGroupUsers = async (id: string, groupName: string): Promise<void> => {
    if (!id) return;
    // Show modal without users if user group is not found (e.g. approver role is removed or user group is deleted)
    const users = (await getApproverUserGroupRequest(id))?.users || [];
    const userModalContent = (
      <>
        {users.slice(0, 10).map((user, i) => (
          <BodyText key={i}>{user}</BodyText>
        ))}
        {users.length > 10 ? <BodyText>...</BodyText> : null}
      </>
    );
    dispatch(
      modalShow(
        groupName,
        '',
        '',
        () => {},
        () => dispatch(modalClose()),
        userModalContent,
      ),
    );
  };

  const customApproverElement = (approvalChainStep: ApprovalChainStepResponse): JSX.Element | undefined => {
    switch (approvalChainStep.type) {
      case ApproverType.UserGroup:
        return (
          <BodyText clickableAppearance color="purple500" onClick={() => showUserGroupUsers(approvalChainStep.id, approvalChainStep.name)}>
            {approvalChainStep.name}
          </BodyText>
        );
      case ApproverType.User:
        // Don't customize
        return undefined;
    }
  };

  const createApprovalChainProp = () => {
    if (!approvalChain) {
      return undefined;
    }
    return {
      ...approvalChain,
      chain: approvalChain.chain.map(approvalChainStep => ({
        ...approvalChainStep,
        customDisplayedApprover: customApproverElement(approvalChainStep),
      })),
    };
  };

  return (
    <OrderDetailsReusable
      order={order}
      translations={orderDetailsTranslations}
      countries={COUNTRIES}
      mode={OrderDetailsMode.User}
      approvalChain={createApprovalChainProp()}
    />
  );
};

export default OrderDetails;
